import { motion, useSpring, useTransform, useInView, AnimatePresence, cubicBezier, useAnimate, stagger } from 'framer-motion';

import { useRef, useState, useEffect, useContext } from 'react';

import { MotionImage, useSanitizedTranslation, XumoButton as Button, StackedContent } from 'ui';
import { useMeasure } from '@uidotdev/usehooks'

export interface IconGroups {
    title: string;
    icons: any;
}

export interface RowOfIconsProps {
    images: string[];
    reverse: boolean;
    index: number;
}
  
export interface IconEndlessScrollTabsProps {
  bg: string;
  copy: any;
  icontabs: IconGroups[];
  textbox: boolean;
}

const IconRow = ({images, reverse, index}: RowOfIconsProps) => {
  const speed = 100 //px per second
  const [ref, { width }] = useMeasure();
  const [duration, setDuration] = useState<number>(0);

  useEffect(() => {
    if (width && width > 0) {
      setDuration((width) / speed);
    }
  }, [width]);


  const childVariants = {
    initial: { opacity: 0 },
    visible: {
        opacity: [0, 1],
        transition: {
            ease: [0, 1, 1, 1],
            duration: 1.5,
            restDelta: 0.00001,
            restSpeed: 0.00001,
        },
    },
    animate:duration > 0 && {
      x: ['0%', '-50%'],
      transition:{
        repeat: Infinity,
        repeatType: 'loop',
        duration: duration,
        ease: 'linear',
      }
    }
  }

  const childVariantsNegative = {
    initial: { opacity: 0 },
    visible: {
        opacity: [0, 1],
        transition: {
            ease: [0, 1, 1, 1],
            duration: 1.5,
            restDelta: 0.00001,
            restSpeed: 0.00001,
        },
    },
    animate:duration > 0 && {
      x: ['0%', '50%'],
      transition:{
        repeat: Infinity,
        repeatType: 'loop',
        duration: duration,
        ease: 'linear',
      }
    }
  }

  //arrays map in backwards due to flex reverse
  const imagesArray = !reverse ? [...images, ...images].reverse() : [...images, ...images]


return(
  <div className='flex' key={`item-${index}`}>
      {/* Spacer */}
      <div className="aspect-[16/7] w-[45vw] max-w-[400px] xl:w-[20vw] " />
      {/* Animated row */}
      <motion.div
        className={`absolute flex ${reverse ? 'flex-row items-start left-0' : 'flex-row-reverse items-end right-0'} gap-2 xl:gap-4`}                       
        variants={reverse ? childVariants : childVariantsNegative}
        whileInView="visible"
        initial="initial"
        animate="animate"
        ref={ref}
      >
      {/* Mapping images twice for infinite scroll illusion */}
      {imagesArray.map((item: any, index: number) => (
          <motion.div
            key={'altmain' + index}
            className="relative inline-block aspect-[16/7] w-[45vw] max-w-[400px] overflow-hidden rounded-xl shadow-xl xl:w-[20vw]"
          >
          <AnimatePresence>
              <MotionImage
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{
                  opacity: 1,
                  scale: 1.0,
                  transition: { ease: 'easeOut', restDelta: 0.00001, duration: 0.4, delay: 0.0075 * index },
              }}
              exit={{ opacity: 0, transition: { delay: 0.5 } }}
              key={'img-main' + index + item}
              src={item}
              alt="partner logo"
              fill
              className="object-cover"
              sizes="40vw"
              />
          </AnimatePresence>
          </motion.div>
      ))}
      </motion.div> 
    </div>
)
}

export const IconEndlessScrollTabs = ({ copy, bg="white", icontabs, textbox }: IconEndlessScrollTabsProps) => {

  const { t } = useSanitizedTranslation();

  const [tab, setTab] = useState(0);

  const currentTab = icontabs[tab]

  const variants = {
    visible: {
      transition: {
        delayChildren: 0,
        staggerChildren: 0.02,
        restDelta: 0.0001,
        restSpeed: 0.0001,
      },
    },
  };

  return (
    <motion.section className="relative z-40 flex flex-col w-full justify-center" data-testid={'text-section'} style={{background: bg}}>
      <div className="wrapper">
        {/* Textbox */}
        <div className={`py-16 lg:pt-32 lg:pb-36 ${textbox ? 'bg-white' : 'bg-transparent'} rounded-lg`}>
          <div className={`${textbox ? 'content-wrapper-x' : ''} flex flex-col items-center justify-center pb-32`}>
            {/* Copy */}
            <StackedContent
              full
              center
              noOrphans
              content={copy}
            />

            {/* Section buttons */}
            <div className="type-body-base flex w-full flex-wrap items-center justify-center pt-8 text-center tracking-tight max-lg:text-[18px] lg:space-x-2">
            {icontabs.map((item: any, index: number) => (
                <div
                    onClick={() => setTab(index)}
                    data-i18n={item.title}
                    className={
                    (tab === index ? 'weight-600 text-black outline-xumoAltBerryLight' : 'cursor-pointer text-xumoCharcoal outline-transparent hover:text-xumoBlack') +
                    ' no-shift rounded-full px-4 py-1 outline outline-2 max-lg:my-2'
                    }
                >
                    {t(item.title)}
                </div>
            ))}
            </div>
          </div> {/* End text wrapper */}
        {/* Icon Carousels */}
        <motion.div
          initial="initial"
          whileInView="visible"
          variants={variants}
          viewport={{ once: true, margin: '-20% 0% -20% 0%' }}
          className="flex flex-col gap-2 xl:gap-4"
          >
            {/* Carousel row */}
              {currentTab.icons.map((item: any, rowId: number) => (
                <IconRow 
                  key={rowId + 'iconrow'}
                  index={rowId}
                  images={item}
                  reverse={rowId % 2 !== 0}
                />
              ))}
          </motion.div>
        </div> {/* End white textbox */}
     </div> {/* End wrapper */}
    </motion.section>
  );
};